import React, { useCallback, useEffect, useMemo, useState } from "react";
import ConfirmModal from "../../common/ConfirmModal";
import {
  fetchLibraryAutoAssignDocuments,
  saveLibraryAutoAssignPolicy,
  saveLibraryAutoAssignRules,
} from "../qms/qmsService";
import "./QmsLibraryAutoAssignConfig.scss";

function normalizePolicyAction(value) {
  return String(value || "Include").trim().toLowerCase() === "exclude"
    ? "Exclude"
    : "Include";
}

function mapDocumentRow(row) {
  const groupId = String(row?.DocumentGroupIdRef || row?.documentGroupIdRef || "").trim();
  if (!groupId) return null;

  const title = String(row?.DocumentTitle || row?.documentTitle || "").trim() || "Untitled document";
  const version = row?.VersionNumber ?? row?.versionNumber;
  const category = String(row?.DocumentCategoryName || row?.documentCategoryName || "").trim();
  const abbr = String(
    row?.DocumentCategoryAbberiviation || row?.documentCategoryAbberiviation || "",
  ).trim();
  const uniqueNum = String(
    row?.DocumentCategoryUniqueNumber || row?.documentCategoryUniqueNumber || "",
  ).trim();

  return {
    DocumentGroupIdRef: groupId,
    DocumentId: row?.DocumentId || row?.documentId || "",
    DocumentTitle: title,
    VersionNumber: version == null || version === "" ? "—" : version,
    DocumentCategoryName: category || "—",
    DocumentCategoryAbberiviation: abbr,
    DocumentCategoryUniqueNumber: uniqueNum,
    PolicyAction: normalizePolicyAction(row?.PolicyAction || row?.policyAction),
  };
}

function formatDocLabel(row) {
  const code =
    row.DocumentCategoryAbberiviation && row.DocumentCategoryUniqueNumber
      ? `${row.DocumentCategoryAbberiviation}-${row.DocumentCategoryUniqueNumber}`
      : row.DocumentCategoryUniqueNumber || "";
  return {
    title: row.DocumentTitle,
    meta: [code, row.DocumentCategoryName !== "—" ? row.DocumentCategoryName : "", `v${row.VersionNumber}`]
      .filter(Boolean)
      .join(" · "),
  };
}

function filterRowsBySearch(rows, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => {
    const label = formatDocLabel(row);
    return `${label.title} ${label.meta}`.toLowerCase().includes(q);
  });
}

function InfoHint({ text }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`qms-lib-auto-hint${open ? " is-open" : ""}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="qms-lib-auto-hint__btn"
        aria-label={text}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <i className="fas fa-info-circle" aria-hidden="true" />
      </button>
      <div className="qms-lib-auto-hint__popover" role="tooltip">
        {text}
      </div>
    </div>
  );
}

function DocumentListPanel({
  title,
  infoText,
  rows,
  selectedIds,
  onItemClick,
  emptyText,
  noMatchText,
  loading,
  searchPlaceholder = "Search...",
}) {
  const [search, setSearch] = useState("");
  const filteredRows = useMemo(() => filterRowsBySearch(rows, search), [rows, search]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <div className="qms-lib-auto-panel">
      <div className="qms-lib-auto-panel__head">
        <div className="qms-lib-auto-panel__title-row">
          <InfoHint text={infoText} />
          <h6 className="mb-0 qms-lib-auto-panel__heading">{title}</h6>
        </div>
        <input
          type="search"
          className="form-control form-control-sm qms-lib-auto-panel__search"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="qms-lib-auto-panel__hint">
          Click to move · Ctrl+click to select · Shift+click range
        </span>
      </div>
      <div className="qms-lib-auto-panel__list">
        {loading ? <div className="qms-lib-auto-panel__empty">Loading...</div> : null}
        {!loading && !rows.length ? (
          <div className="qms-lib-auto-panel__empty">{emptyText}</div>
        ) : null}
        {!loading && rows.length && !filteredRows.length ? (
          <div className="qms-lib-auto-panel__empty">{noMatchText}</div>
        ) : null}
        {!loading
          ? filteredRows.map((row) => {
              const label = formatDocLabel(row);
              const selected = selectedSet.has(row.DocumentGroupIdRef);
              return (
                <button
                  key={row.DocumentGroupIdRef}
                  type="button"
                  className={`qms-lib-auto-panel__item${selected ? " is-selected" : ""}`}
                  onClick={(event) => onItemClick(row, event, filteredRows)}
                >
                  <span className="qms-lib-auto-panel__title">{label.title}</span>
                  <span className="qms-lib-auto-panel__meta">{label.meta}</span>
                </button>
              );
            })
          : null}
      </div>
    </div>
  );
}

function QmsLibraryAutoAssignConfig({
  userData,
  enabled,
  onEnabledChange,
  showAlert,
  showLoader,
  hideLoader,
}) {
  const [included, setIncluded] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIncludeIds, setSelectedIncludeIds] = useState([]);
  const [selectedExcludeIds, setSelectedExcludeIds] = useState([]);
  const [includeAnchorId, setIncludeAnchorId] = useState("");
  const [excludeAnchorId, setExcludeAnchorId] = useState("");
  const [policyModal, setPolicyModal] = useState({
    open: false,
    nextEnabled: enabled,
  });

  const clearSelections = useCallback(() => {
    setSelectedIncludeIds([]);
    setSelectedExcludeIds([]);
    setIncludeAnchorId("");
    setExcludeAnchorId("");
  }, []);

  const applyRowsToLists = useCallback((rows) => {
    const mapped = (rows || []).map(mapDocumentRow).filter(Boolean);
    setIncluded(mapped.filter((row) => row.PolicyAction === "Include"));
    setExcluded(mapped.filter((row) => row.PolicyAction === "Exclude"));
    clearSelections();
  }, [clearSelections]);

  const moveToExcluded = useCallback((ids) => {
    const idSet = new Set((ids || []).map((id) => String(id)).filter(Boolean));
    if (!idSet.size) return;

    setIncluded((prev) => {
      const moving = prev.filter((item) => idSet.has(item.DocumentGroupIdRef));
      if (moving.length) {
        setExcluded((ex) => [
          ...ex,
          ...moving.map((row) => ({ ...row, PolicyAction: "Exclude" })),
        ]);
      }
      return prev.filter((item) => !idSet.has(item.DocumentGroupIdRef));
    });
    setSelectedIncludeIds([]);
    setIncludeAnchorId("");
  }, []);

  const moveToIncluded = useCallback((ids) => {
    const idSet = new Set((ids || []).map((id) => String(id)).filter(Boolean));
    if (!idSet.size) return;

    setExcluded((prev) => {
      const moving = prev.filter((item) => idSet.has(item.DocumentGroupIdRef));
      if (moving.length) {
        setIncluded((inc) => [
          ...inc,
          ...moving.map((row) => ({ ...row, PolicyAction: "Include" })),
        ]);
      }
      return prev.filter((item) => !idSet.has(item.DocumentGroupIdRef));
    });
    setSelectedExcludeIds([]);
    setExcludeAnchorId("");
  }, []);

  const selectRange = useCallback((filteredRows, anchorId, targetId) => {
    const ids = filteredRows.map((row) => row.DocumentGroupIdRef);
    const anchorIndex = ids.indexOf(anchorId);
    const targetIndex = ids.indexOf(targetId);
    if (anchorIndex < 0 || targetIndex < 0) return [targetId];
    const [start, end] =
      anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
    return ids.slice(start, end + 1);
  }, []);

  const handleIncludeItemClick = useCallback(
    (row, event, filteredRows) => {
      const id = row.DocumentGroupIdRef;
      if (event.ctrlKey || event.metaKey) {
        setSelectedIncludeIds((prev) =>
          prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
        );
        setIncludeAnchorId(id);
        return;
      }
      if (event.shiftKey && includeAnchorId) {
        setSelectedIncludeIds(selectRange(filteredRows, includeAnchorId, id));
        return;
      }
      moveToExcluded([id]);
    },
    [includeAnchorId, moveToExcluded, selectRange],
  );

  const handleExcludeItemClick = useCallback(
    (row, event, filteredRows) => {
      const id = row.DocumentGroupIdRef;
      if (event.ctrlKey || event.metaKey) {
        setSelectedExcludeIds((prev) =>
          prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
        );
        setExcludeAnchorId(id);
        return;
      }
      if (event.shiftKey && excludeAnchorId) {
        setSelectedExcludeIds(selectRange(filteredRows, excludeAnchorId, id));
        return;
      }
      moveToIncluded([id]);
    },
    [excludeAnchorId, moveToIncluded, selectRange],
  );

  const loadDocuments = useCallback(async () => {
    if (!userData?.OrganizationId) return;
    setLoadingDocs(true);
    try {
      const result = await fetchLibraryAutoAssignDocuments(userData);
      if (!result.success) {
        throw new Error(result.message || "Could not load published documents.");
      }
      applyRowsToLists(result.rows);
    } catch (error) {
      showAlert?.({
        title: "Library auto-assign",
        message: error?.message || "Could not load published documents.",
        type: "danger",
      });
      setIncluded([]);
      setExcluded([]);
      clearSelections();
    } finally {
      setLoadingDocs(false);
    }
  }, [applyRowsToLists, clearSelections, showAlert, userData]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const requestPolicyChange = (nextEnabled) => {
    if (nextEnabled === enabled) return;
    setPolicyModal({ open: true, nextEnabled });
  };

  const confirmPolicyChange = async () => {
    const nextEnabled = policyModal.nextEnabled;
    setPolicyModal({ open: false, nextEnabled });
    showLoader?.("Saving auto-assign policy...");
    try {
      const result = await saveLibraryAutoAssignPolicy(userData, {
        libraryAutoAssignEnabled: nextEnabled,
      });
      if (!result.success) throw new Error(result.message || "Save failed.");
      onEnabledChange?.(nextEnabled);
      showAlert?.({
        title: "Saved",
        message: nextEnabled
          ? "Auto-assign is now enabled."
          : "Auto-assign is now disabled.",
        type: "success",
      });
      if (nextEnabled) await loadDocuments();
    } catch (error) {
      showAlert?.({
        title: "Error",
        message: error?.message || "Could not save auto-assign policy.",
        type: "danger",
      });
    } finally {
      hideLoader?.();
    }
  };

  const handleSaveRules = async () => {
    if (!userData?.OrganizationId) return;
    const allRows = [
      ...included.map((row) => ({ ...row, PolicyAction: "Include" })),
      ...excluded.map((row) => ({ ...row, PolicyAction: "Exclude" })),
    ];
    if (!allRows.length) {
      showAlert?.({
        title: "Nothing to save",
        message: "No published documents found to save.",
        type: "warning",
      });
      return;
    }

    setSaving(true);
    showLoader?.("Saving document lists...");
    try {
      const result = await saveLibraryAutoAssignRules(userData, allRows);
      if (!result.success) throw new Error(result.message || "Save failed.");
      showAlert?.({
        title: "Saved",
        message: result.message || "Included and excluded documents saved.",
        type: "success",
      });
      await loadDocuments();
    } catch (error) {
      showAlert?.({
        title: "Error",
        message: error?.message || "Could not save document lists.",
        type: "danger",
      });
    } finally {
      hideLoader?.();
      setSaving(false);
    }
  };

  const busy = saving || loadingDocs;
  const includeMoveCount = selectedIncludeIds.length;
  const excludeMoveCount = selectedExcludeIds.length;

  return (
    <>
      <div className="card shadow-sm capa-config-saas__panel-card qms-lib-auto-config-card">
        <div className="card-body qms-lib-auto-config-body">
          <div className="qms-lib-auto-config__header">
            <InfoHint text="Latest published version of each document is shown. Click a document to move it, or Ctrl+click to select multiple and use the arrow buttons." />
            <h6 className="mb-0 qms-lib-auto-config__heading">Document Library — auto assignment</h6>
          </div>

          <div className="form-check form-switch qms-lib-auto-config__toggle">
            <input
              className="form-check-input"
              type="checkbox"
              id="qms-library-auto-assign-enabled"
              checked={Boolean(enabled)}
              onChange={(e) => requestPolicyChange(e.target.checked)}
            />
            <label className="form-check-label qms-lib-auto-config__toggle-label" htmlFor="qms-library-auto-assign-enabled">
              Auto-assign published documents to eligible users
            </label>
          </div>

          <div className="qms-lib-auto-dual">
            <DocumentListPanel
              title="Included documents"
              infoText="Documents in this list will be auto-assigned to eligible users."
              rows={included}
              selectedIds={selectedIncludeIds}
              onItemClick={handleIncludeItemClick}
              emptyText="No included documents."
              noMatchText="No matching included documents."
              searchPlaceholder="Search included..."
              loading={loadingDocs}
            />

            <div className="qms-lib-auto-dual__actions">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                title={
                  includeMoveCount > 0
                    ? `Move ${includeMoveCount} to excluded`
                    : "Select documents first (Ctrl+click)"
                }
                disabled={busy || includeMoveCount === 0}
                onClick={() => moveToExcluded(selectedIncludeIds)}
              >
                <i className="fas fa-chevron-right" />
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                title={
                  excludeMoveCount > 0
                    ? `Move ${excludeMoveCount} to included`
                    : "Select documents first (Ctrl+click)"
                }
                disabled={busy || excludeMoveCount === 0}
                onClick={() => moveToIncluded(selectedExcludeIds)}
              >
                <i className="fas fa-chevron-left" />
              </button>
            </div>

            <DocumentListPanel
              title="Excluded documents"
              infoText="Documents in this list will not be auto-assigned."
              rows={excluded}
              selectedIds={selectedExcludeIds}
              onItemClick={handleExcludeItemClick}
              emptyText="No excluded documents."
              noMatchText="No matching excluded documents."
              searchPlaceholder="Search excluded..."
              loading={loadingDocs}
            />
          </div>

          <div className="d-flex justify-content-end qms-lib-auto-config__footer">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleSaveRules}
              disabled={busy}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={policyModal.open}
        onClose={() => setPolicyModal({ open: false, nextEnabled: enabled })}
        onConfirm={confirmPolicyChange}
        title={policyModal.nextEnabled ? "Enable auto-assign?" : "Disable auto-assign?"}
        message={
          policyModal.nextEnabled
            ? "Eligible users will automatically receive included published documents. Continue?"
            : "New automatic assignments will stop. Existing library access is unchanged until you save document lists. Continue?"
        }
        confirmText="Save"
        confirmButtonClass="btn-primary"
        icon={
          policyModal.nextEnabled
            ? "fas fa-toggle-on text-primary"
            : "fas fa-toggle-off text-secondary"
        }
      />
    </>
  );
}

export default QmsLibraryAutoAssignConfig;
